/**
 * Runtime feature flags — content hubs that are not yet ready for end users.
 *
 * Toggle here to expose/hide a hub WITHOUT touching its component or DB tables.
 * Admin UIs (/admin/*) bypass these flags by design — admins keep authoring.
 *
 * Set to `true` only when:
 *   • base table has enough curated content
 *   • UX flow has been validated end-to-end
 *   • Simone explicitly approved the launch
 *
 * Sprint 3.B.2 — 2026-05-12
 */

export const FEATURES = {
  /**
   * Entrada invertida (08/09/2026): a home passa a ser o Organizador aberto — a
   * mãe escreve o dia, vê a agenda montada e só então cria conta. Ligar aqui
   * troca a `/` da landing antiga pela nova entrada; /entrada dá acesso direto
   * para revisão enquanto isto está desligado.
   */
  entradaAgendaPrimeiro: false,

  /** Hub Profissionais (rede de parceiras) — MaternaPlus › Profissionais. Tabela `professionals` vazia. */
  hubParceiras: false,

  /** Hub Eventos da Comunidade — consumer-facing. `adm_community_events` com 0 rows. */
  hubEventosComunidade: false,

  /** Bloco Indicações (BibliotecaMaterna). `adm_indicacoes` com 7 rows — abaixo do mínimo. */
  blocoIndicacoes: false,

  /** Tile MaternaBox em Mais Ferramentas + página /maternar/materna-box. Roadmap futuro. */
  hubMaternaBox: false,

  /** Aba "Comunidade" (mural de histórias) dentro do MaternaPlus. Roadmap futuro. */
  maternaPlusComunidade: false,

  /** Tile "Materna+" fica oculto até haver conteúdo real cadastrado (decisão Sessão O — pré-lançamento B2C). */
  maternaPlusVisible: false,

  /** Predictive Leaps (dev.proximo_salto / dev.patterns / dev.twin) — Sessão O. Reativa em 2-3 meses quando houver volume de dados. */
  predictiveLeapsVisible: false,

  /** Indique e Ganhe (ReferralCard/Banner) — Sessão O. Reativa em 30-60 dias quando base estabilizar. Edges permanecem ativas. */
  referralsVisible: false,

  /** Partner Mode (PartnerModeCard + rota /parceiro/:token + edge partner-view) — Sessão O. Risco LGPD; aguarda revisão de segurança formal do auth-by-token. */
  partnerModeVisible: false,

  /**
   * Fase 2 — PlanoDaSemanaCard no /perfil (2026-07-29).
   * Renderiza os itens do plano do Momento Wow (source='onboarding_wow')
   * diretamente na home, abaixo do Ritual. Nasce false — só liga em produção
   * depois de aprovação explícita e A/B contra baseline docs/memory/baseline-wow-fase2-2026-07-29.md.
   */
  wowPlanCardHome: true,

  /**
   * Item de hoje, sob demanda (12/08/2026) — ItemDeHojeCard no /perfil.
   * Nada é gerado sem a mãe pedir. Desligado até a Etapa 0 (espaço de produtividade Notion-like) existir.
   */
  dailyItemOnDemand: false,

  /**
   * Organizador de Rotina Etapa 0 (13/08/2026) — RotinaBaseCard no /perfil.
   * Assistente de produtividade sem IA: 3 perguntas, gerador determinístico e
   * salva compromissos em série. Começa false para validação interna.
   */
  organizadorRotinaV0: true,

  /**
   * /perfil em jornada de 3 passos (02/11/2026). Desligada = tela atual
   * exatamente como antes. Ligada: um aviso por visita, sem SessionGreeting
   * nem popup de oferta, e oferta única no fim só para free sem teste.
   */
  perfilJornadaV1: true,

  /**
   * Passo "Primeiro compromisso" no fim do cadastro (02/11/2026) — entre o
   * Momento Wow e a oferta, a mãe agenda o primeiro compromisso da semana e
   * recebe o convite de notificação. Decisão: quem cria compromisso E ativa
   * push nas primeiras 24h volta muito mais (análise de retenção 02/11).
   * Desligar aqui devolve o fluxo Wow → oferta exatamente como antes.
   */
  onboardingPrimeiroCompromisso: true,

  /**
   * Fase 1 — nova hierarquia da tela diária /perfil (2026-07-31).
   * Ação primeiro (atalhos + cards de ação logo após a saudação), upsell/nudges
   * reagrupados em slot único com exclusividade, cards de conteúdo agrupados
   * visualmente. Nasce FALSE: sem a flag, a tela renderiza exatamente como hoje.
   * Ativar por browser com /perfil?ff=dailyHierarchyV2.
   */
  dailyHierarchyV2: false,

  /**
   * Fase 1 — cobertura do guard central de overlays (2026-08-01).
   * Registra overlays que hoje estão fora do overlayManager (começando pelo
   * PaywallModal) para ganhar exclusividade/força-fechamento central.
   * Nasce FALSE: sem a flag, o paywall abre exatamente como hoje.
   * Ativar por browser com /perfil?ff=overlayManagerCoverage.
   */
  overlayManagerCoverage: true,

  /**
   * Fase 1 — NotificationDrawer dentro do guard central de overlays (2026-08-02).
   * Registra o drawer com prioridade NOTIFICATION_DRAWER (420) e preserva a
   * intenção do deep-link `?notif=1` (sessionStorage, TTL 180s) para reabrir
   * automaticamente quando um overlay superior deixar o caminho livre.
   * Ligada em 2026-08-02: sem ela o `useOverlay` do drawer não roda, logo a
   * intenção pendente nunca era gravada e o drawer não reabria depois do
   * spotlight. Desligar por browser com /perfil?ff_off=notificationDrawerGuard.
   */
  notificationDrawerGuard: true,
} as const;

export type FeatureFlag = keyof typeof FEATURES;

/**
 * Override 100% browser-local para preview/staff.
 *
 * Ativação:  qualquer URL com `?ff=<flag>` (ex: /perfil?ff=wowPlanCardHome)
 *            → grava `m360:ff_override:<flag>=1` no localStorage DAQUELE navegador.
 * Desativar: `?ff_off=<flag>` (ou `?ff_off=all` limpa todos).
 *
 * Escopo: só afeta a aba/dispositivo que carregou a URL. Nenhuma outra usuária vê
 * mudança. Não persiste em backend. Não altera `FEATURES` em produção.
 */
const OVERRIDE_PREFIX = "m360:ff_override:";

function readOverride(key: FeatureFlag): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(OVERRIDE_PREFIX + key);
    if (v === "1") return true;
    if (v === "0") return false;
    return null;
  } catch {
    return null;
  }
}

/** Processa `?ff=` / `?ff_off=` uma vez no boot. Idempotente. */
export function applyFeatureFlagOverridesFromURL(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const on = params.getAll("ff");
    const off = params.getAll("ff_off");
    const known = Object.keys(FEATURES) as FeatureFlag[];
    let touched = false;
    for (const k of on) {
      if ((known as string[]).includes(k)) {
        window.localStorage.setItem(OVERRIDE_PREFIX + k, "1");
        touched = true;
        console.info(`[features] override ON (browser-local): ${k}`);
      }
    }
    for (const k of off) {
      if (k === "all") {
        for (const key of known) window.localStorage.removeItem(OVERRIDE_PREFIX + key);
        touched = true;
        console.info("[features] all overrides cleared (browser-local)");
      } else if ((known as string[]).includes(k)) {
        window.localStorage.removeItem(OVERRIDE_PREFIX + k);
        touched = true;
        console.info(`[features] override OFF (browser-local): ${k}`);
      }
    }
    if (touched) {
      // Remove ff/ff_off da URL sem recarregar, pra não vazar em prints/share.
      params.delete("ff");
      params.delete("ff_off");
      const qs = params.toString();
      const clean = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", clean);
    }
  } catch {
    /* noop */
  }
}

export const isFeatureEnabled = (key: FeatureFlag): boolean => {
  const override = readOverride(key);
  if (override !== null) return override;
  return FEATURES[key];
};

